'use client';

import { TranslationKeys } from '@/components/language/i18n';
import Translate from '@/components/language/Translate';
import { MenuItems } from '@/components/layout/Menu/MenuItems';
import { noop } from 'lodash-es';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

export const useCurrentPage = ({
  onChangeRoute = noop,
}: { onChangeRoute?: (url: string) => void } = {}) => {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';

  const fullPathName = usePathname() || '/';
  //console.log('🚀 ~ fullPathName:', fullPathName);
  // Rimuovi il basePath dal pathName
  let pathName = fullPathName.replace(basePath, '') || '/';
  //console.log('🚀 ~ pathName:', pathName);
  pathName = pathName.replace(/\/$/, '');
  //console.log('🚀 ~ pathName:', pathName);

  //https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
  const searchParams = 'useSearchParams();';

  const isAdminPage = useMemo(() => pathName.includes('/admin'), [pathName]);
  const showSearch = ['/secureInBox', '/secureSync', '/secureVault'].includes(
    pathName,
  );
  let currentPage =
    MenuItems.find((item) => {
      if (item.href === pathName) return true;
      if (item.children) {
        return item.children.find((subItem) => subItem.href === pathName);
      }
    }) || MenuItems[0];

  const isHome = pathName === '/home';

  const unloggedPage = [
    '/',
    '/login',
    '/password',
    '/register',
    '/smartshare',
    '/download',
  ].includes(pathName);
  const hideMenu = unloggedPage;
  const showBreadcrumbs = ![
    '/',
    '/login',
    '/password',
    '/register',
    '/home',
  ].includes(pathName);

  const isSingle = pathName.split('/').length >= 3;

  const fullUrl = useMemo(
    () => `${pathName}?${searchParams}`,
    [pathName, searchParams],
  );

  useEffect(() => {
    onChangeRoute(fullUrl);
  }, [fullUrl, onChangeRoute]);

  // console.dir(
  //   {
  //     currentPage,
  //     pathName,
  //     isHome,
  //     isSingle,
  //     fullUrl,
  //     isAdminPage,
  //     unloggedPage,
  //     hideMenu,
  //     showBreadcrumbs,
  //     showSearch,
  //   },
  //   { depth: null },
  // );
  return {
    currentPage,

    pathName,
    isHome,
    isSingle,

    fullUrl,
    isAdminPage,
    unloggedPage,
    hideMenu,
    showBreadcrumbs,
    showSearch,
  };
};

export const useTripleTap = (onTripleTap: Function, { timeout = 500 } = {}) => {
  const [tapCount, setTapCount] = useState(0);

  useEffect(() => {
    // Se il conteggio dei tocchi raggiunge 3, esegui la callback e resetta il conteggio
    if (tapCount === 3) {
      onTripleTap();
      setTapCount(0);
    }

    // Se non ci sono altri tocchi entro il timeout, resetta il conteggio
    const timer = setTimeout(() => {
      if (tapCount > 0) {
        setTapCount(0);
      }
    }, timeout);

    return () => clearTimeout(timer);
  }, [tapCount, onTripleTap, timeout]);

  // Funzione da chiamare al tocco/click dell'elemento
  const handleTap = () => setTapCount(tapCount + 1);

  return handleTap;
};

export const useNavigation = () => {
  const router = useRouter();

  const navigate = (url: string) => {
    router.push(url);
  };

  const goHome = () => {
    router.push('/home');
  };

  const goLogin = () => {
    router.replace('/login');
  };

  const goBack = () => {
    router.back();
  };

  const refresh = () => {
    router.refresh();
  };

  const replace = (url: string) => {
    router.replace(url);
  };

  const goToProfile = () => {
    router.push('/profile');
  };

  const goToSettings = () => {
    router.push('/settings');
  };

  const goToPage = (url: string) => {
    router.push(url);
  };

  return {
    navigate,
    goLogin,
    goHome,
    goBack,
    refresh,
    replace,
    goToProfile,
    goToSettings,
    goToPage,
    router,
  };
};

export const useTitle = (
  title: string | React.ReactNode,
): string | React.ReactNode => {
  const theTitle = useMemo(() => {
    if (isTranslateKey(title)) {
      return <Translate i18nKey={title as TranslationKeys} />;
    }
    return title;
  }, [title]);

  return theTitle;
};

export const isTranslateKey = (title: string | React.ReactNode): boolean => {
  return typeof title === 'string' && title.includes('.');
};
